import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Loader2, Save, Plus, Trash2, Megaphone } from "lucide-react";

const API = "/api";

const FIELD_LABEL = {
  support_email: "Email de soporte",
  support_phone: "Teléfono de soporte",
  support_whatsapp: "WhatsApp de soporte",
  support_hours: "Horario de atención",
  support_channels: "Canales de atención",
  legal_name: "Razón social",
  public_brand_name: "Marca pública",
  country: "País",
  business_registration: "RUC / registro",
  address: "Dirección",
  contact_identity: "Identidad de contacto",
  android_store_url: "URL Google Play",
  ios_store_url: "URL App Store",
};

export default function AdminSharedContent() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [support, setSupport] = useState({});
  const [company, setCompany] = useState({});
  const [appLinks, setAppLinks] = useState({});
  const [faqDraft, setFaqDraft] = useState(null);
  const [noticeDraft, setNoticeDraft] = useState(null);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/shared-content`);
      setData(r.data);
      setSupport(r.data.support || {});
      setCompany(r.data.company || {});
      setAppLinks(r.data.app_links || {});
    } catch (_e) {
      toast.error("Error al cargar contenido compartido");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBlock = async (path, values, label) => {
    setWorking(true);
    try {
      await axios.put(`${API}/admin/shared-content/${path}`, { values });
      toast.success(`${label} guardado`);
      load();
    } catch (_e) { toast.error(`Error al guardar ${label}`); } finally { setWorking(false); }
  };

  const bridge = async () => {
    setWorking(true);
    try {
      const r = await axios.post(`${API}/admin/shared-content/bridge`);
      toast.success(`Migrados: soporte ${r.data.report.support}, empresa ${r.data.report.company}, FAQ ${r.data.report.faq}`);
      load();
    } catch (_e) { toast.error("Error en la migración"); } finally { setWorking(false); }
  };

  const saveFaq = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/admin/shared-content/faq`, faqDraft);
      toast.success("FAQ guardada");
      setFaqDraft(null);
      load();
    } catch (_e) { toast.error("Error al guardar la FAQ"); } finally { setWorking(false); }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm("¿Eliminar esta pregunta?")) return;
    try {
      await axios.delete(`${API}/admin/shared-content/faq/${id}`);
      load();
    } catch (_e) { toast.error("Error al eliminar"); }
  };

  const saveNotice = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/admin/shared-content/service-notices`, noticeDraft);
      toast.success("Aviso guardado");
      setNoticeDraft(null);
      load();
    } catch (_e) { toast.error("Error al guardar el aviso"); } finally { setWorking(false); }
  };

  const deleteNotice = async (id) => {
    if (!window.confirm("¿Eliminar este aviso?")) return;
    try {
      await axios.delete(`${API}/admin/shared-content/service-notices/${id}`);
      load();
    } catch (_e) { toast.error("Error al eliminar"); }
  };

  const renderBlock = (title, fields, values, setValues, path, testid) => (
    <section aria-labelledby={`${testid}-heading`}
             className="bg-[#0B2545] border border-white/10 rounded-xl p-5 mb-6" data-testid={testid}>
      <h2 id={`${testid}-heading`} className="text-base md:text-lg font-semibold text-white mb-4">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f}>
            <Label htmlFor={`${testid}-${f}`} className="text-slate-300 text-sm">{FIELD_LABEL[f] || f}</Label>
            <Input id={`${testid}-${f}`} value={values[f] || ""}
                   onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                   className="mt-1 bg-[#14375F] border-white/15 text-white"
                   data-testid={`${testid}-field-${f}`} />
          </div>
        ))}
      </div>
      <Button onClick={() => saveBlock(path, values, title)} disabled={working}
              className="mt-4 bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
              data-testid={`${testid}-save`}>
        <Save className="w-4 h-4 mr-1" aria-hidden="true" /> Guardar
      </Button>
    </section>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071B2F] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#5BC0FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071B2F] pb-16">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
              data-testid="admin-shared-back">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver al admin
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Contenido compartido</h1>
        <p className="text-slate-400 text-sm md:text-base mb-6">
          Una sola edición que consumen la web, Android y iOS por la misma API.
        </p>

        <div className="flex flex-wrap gap-3 mb-6">
          <Button onClick={bridge} variant="ghost" className="text-slate-300"
                  data-testid="admin-shared-bridge">Importar desde contenido del sitio</Button>
          <Link to="/admin/site-content">
            <Button variant="ghost" className="text-slate-300"
                    data-testid="admin-shared-site-link">Contenido del sitio (legacy)</Button>
          </Link>
          <Link to="/admin/legal">
            <Button variant="ghost" className="text-slate-300"
                    data-testid="admin-shared-legal-link">Documentos legales</Button>
          </Link>
          <Link to="/admin/pricing-display">
            <Button variant="ghost" className="text-slate-300"
                    data-testid="admin-shared-pricing-link">Precios</Button>
          </Link>
        </div>

        {renderBlock("Soporte", data.support_fields, support, setSupport, "support", "admin-shared-support")}
        {renderBlock("Identidad de la empresa", data.company_fields, company, setCompany, "company", "admin-shared-company")}
        {renderBlock("Enlaces de las apps", data.app_link_fields, appLinks, setAppLinks, "app-links", "admin-shared-applinks")}

        <section aria-labelledby="faq-heading"
                 className="bg-[#0B2545] border border-white/10 rounded-xl p-5 mb-6" data-testid="admin-shared-faq">
          <div className="flex items-center justify-between mb-4">
            <h2 id="faq-heading" className="text-base md:text-lg font-semibold text-white">FAQ</h2>
            <Button onClick={() => setFaqDraft({ locale: "es", question: "", answer: "", active: true, sort_order: 0 })}
                    className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
                    data-testid="admin-shared-faq-new">
              <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nueva
            </Button>
          </div>
          {(data.faq || []).length === 0 ? (
            <p className="text-slate-500 text-sm">Sin preguntas registradas.</p>
          ) : (
            <ul className="space-y-3">
              {data.faq.map((f) => (
                <li key={f.id} className="border border-white/10 rounded-lg p-3"
                    data-testid={`admin-faq-item-${f.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-white text-sm font-medium">{f.question}</p>
                      <p className="text-slate-400 text-xs mt-1">{f.answer}</p>
                      <p className="text-slate-500 text-xs mt-1">
                        {f.locale} · orden {f.sort_order} · {f.active ? "activa" : "inactiva"}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setFaqDraft({ ...f })} aria-label="Editar pregunta"
                              className="p-1.5 rounded hover:bg-white/10 text-slate-300"
                              data-testid={`admin-faq-edit-${f.id}`}>
                        <Save className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button onClick={() => deleteFaq(f.id)} aria-label="Eliminar pregunta"
                              className="p-1.5 rounded hover:bg-red-500/10 text-red-400"
                              data-testid={`admin-faq-delete-${f.id}`}>
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="notices-heading"
                 className="bg-[#0B2545] border border-white/10 rounded-xl p-5" data-testid="admin-shared-notices">
          <div className="flex items-center justify-between mb-4">
            <h2 id="notices-heading" className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-amber-400" aria-hidden="true" /> Avisos de servicio
            </h2>
            <Button onClick={() => setNoticeDraft({ locale: "es", title: "", body: "", severity: "info", active: true })}
                    className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
                    data-testid="admin-shared-notice-new">
              <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nuevo
            </Button>
          </div>
          {(data.service_notices || []).length === 0 ? (
            <p className="text-slate-500 text-sm">Sin avisos.</p>
          ) : (
            <ul className="space-y-3">
              {data.service_notices.map((n) => (
                <li key={n.id} className="border border-white/10 rounded-lg p-3"
                    data-testid={`admin-notice-item-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-white text-sm font-medium">{n.title}</p>
                      <p className="text-slate-400 text-xs mt-1">{n.body}</p>
                      <p className="text-slate-500 text-xs mt-1">
                        {n.locale} · {n.severity} · {n.active ? "activo" : "inactivo"}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setNoticeDraft({ ...n })} aria-label="Editar aviso"
                              className="p-1.5 rounded hover:bg-white/10 text-slate-300"
                              data-testid={`admin-notice-edit-${n.id}`}>
                        <Save className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button onClick={() => deleteNotice(n.id)} aria-label="Eliminar aviso"
                              className="p-1.5 rounded hover:bg-red-500/10 text-red-400"
                              data-testid={`admin-notice-delete-${n.id}`}>
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {faqDraft && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
               role="dialog" aria-modal="true" aria-label="Editar FAQ" data-testid="admin-faq-modal">
            <div className="bg-[#0B2545] border border-white/15 rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-white mb-4">Pregunta frecuente</h2>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="faq-locale" className="text-slate-300 text-sm">Idioma</Label>
                  <select id="faq-locale" value={faqDraft.locale}
                          onChange={(e) => setFaqDraft({ ...faqDraft, locale: e.target.value })}
                          className="w-full bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                          data-testid="admin-faq-field-locale">
                    {(data.locales || []).map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="faq-question" className="text-slate-300 text-sm">Pregunta</Label>
                  <Input id="faq-question" value={faqDraft.question}
                         onChange={(e) => setFaqDraft({ ...faqDraft, question: e.target.value })}
                         className="bg-[#14375F] border-white/15 text-white"
                         data-testid="admin-faq-field-question" />
                </div>
                <div>
                  <Label htmlFor="faq-answer" className="text-slate-300 text-sm">Respuesta</Label>
                  <textarea id="faq-answer" value={faqDraft.answer} rows={4}
                            onChange={(e) => setFaqDraft({ ...faqDraft, answer: e.target.value })}
                            className="w-full bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                            data-testid="admin-faq-field-answer" />
                </div>
                <div className="flex gap-4 items-end">
                  <div>
                    <Label htmlFor="faq-order" className="text-slate-300 text-sm">Orden</Label>
                    <Input id="faq-order" type="number" value={faqDraft.sort_order}
                           onChange={(e) => setFaqDraft({ ...faqDraft, sort_order: Number(e.target.value) })}
                           className="bg-[#14375F] border-white/15 text-white w-24"
                           data-testid="admin-faq-field-order" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300 pb-2">
                    <input type="checkbox" checked={!!faqDraft.active}
                           onChange={(e) => setFaqDraft({ ...faqDraft, active: e.target.checked })}
                           data-testid="admin-faq-field-active" /> Activa
                  </label>
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <Button onClick={saveFaq} disabled={working}
                        className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
                        data-testid="admin-faq-save">Guardar</Button>
                <Button variant="ghost" onClick={() => setFaqDraft(null)} className="text-slate-300"
                        data-testid="admin-faq-cancel">Cancelar</Button>
              </div>
            </div>
          </div>
        )}

        {noticeDraft && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
               role="dialog" aria-modal="true" aria-label="Editar aviso" data-testid="admin-notice-modal">
            <div className="bg-[#0B2545] border border-white/15 rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-white mb-4">Aviso de servicio</h2>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="notice-locale" className="text-slate-300 text-sm">Idioma</Label>
                  <select id="notice-locale" value={noticeDraft.locale}
                          onChange={(e) => setNoticeDraft({ ...noticeDraft, locale: e.target.value })}
                          className="w-full bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                          data-testid="admin-notice-field-locale">
                    {(data.locales || []).map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="notice-title" className="text-slate-300 text-sm">Título</Label>
                  <Input id="notice-title" value={noticeDraft.title}
                         onChange={(e) => setNoticeDraft({ ...noticeDraft, title: e.target.value })}
                         className="bg-[#14375F] border-white/15 text-white"
                         data-testid="admin-notice-field-title" />
                </div>
                <div>
                  <Label htmlFor="notice-body" className="text-slate-300 text-sm">Cuerpo</Label>
                  <textarea id="notice-body" value={noticeDraft.body} rows={4}
                            onChange={(e) => setNoticeDraft({ ...noticeDraft, body: e.target.value })}
                            className="w-full bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                            data-testid="admin-notice-field-body" />
                </div>
                <div>
                  <Label htmlFor="notice-severity" className="text-slate-300 text-sm">Severidad</Label>
                  <select id="notice-severity" value={noticeDraft.severity}
                          onChange={(e) => setNoticeDraft({ ...noticeDraft, severity: e.target.value })}
                          className="w-full bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                          data-testid="admin-notice-field-severity">
                    {(data.severities || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="notice-start" className="text-slate-300 text-sm">Inicio (ISO)</Label>
                    <Input id="notice-start" value={noticeDraft.starts_at || ""}
                           onChange={(e) => setNoticeDraft({ ...noticeDraft, starts_at: e.target.value })}
                           className="bg-[#14375F] border-white/15 text-white"
                           data-testid="admin-notice-field-start" />
                  </div>
                  <div>
                    <Label htmlFor="notice-end" className="text-slate-300 text-sm">Fin (ISO)</Label>
                    <Input id="notice-end" value={noticeDraft.ends_at || ""}
                           onChange={(e) => setNoticeDraft({ ...noticeDraft, ends_at: e.target.value })}
                           className="bg-[#14375F] border-white/15 text-white"
                           data-testid="admin-notice-field-end" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={!!noticeDraft.active}
                         onChange={(e) => setNoticeDraft({ ...noticeDraft, active: e.target.checked })}
                         data-testid="admin-notice-field-active" /> Activo
                </label>
              </div>
              <div className="flex gap-3 mt-5">
                <Button onClick={saveNotice} disabled={working}
                        className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
                        data-testid="admin-notice-save">Guardar</Button>
                <Button variant="ghost" onClick={() => setNoticeDraft(null)} className="text-slate-300"
                        data-testid="admin-notice-cancel">Cancelar</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
